import React from 'react';
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Radio,
  Select,
  Switch,
  TimePicker,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import CheckboxGroupField from './fields/CheckboxGroupField';
import RagBadgeField from './fields/RagBadgeField';
import SignatureField from './fields/SignatureField';
import LocationPickerField from './fields/LocationPickerField';
import { formatLocationValue, isLocationValue } from './fields/locationValue';
import AssetPickerField from './fields/AssetPickerField';
import CurrencyField from './fields/CurrencyField';
import PhoneNumberField from './fields/PhoneNumberField';
import GridField from './fields/GridField';
import { cleanGridValue } from './fields/gridValue';

// Needed so `dayjs(value, 'HH:mm')` below actually parses against the given
// format instead of silently falling back to native Date parsing.
dayjs.extend(customParseFormat);

const TIME_FORMAT = 'HH:mm';

/**
 * Every dynamic custom-field behaviour keys off the authoritative `fieldType`
 *
 * Ported from `Pretty/src/pages/Events/customFields/customFieldTypes.tsx`. A new
 * caller must call these functions rather than writing its own switch:
 */

export const RAG_COLORS = ['red', 'amber', 'green'];

export const isRagColor = (value) =>
  typeof value === 'string' && RAG_COLORS.includes(value.toLowerCase());

/**
 * Normalises fieldRules.options.
 */
export const normalizeCustomFieldOptions = (raw) =>
  (raw ?? []).reduce((acc, option) => {
    if (option && typeof option.id === 'string' && option.id && typeof option.label === 'string') {
      acc.push({
        id: option.id,
        label: option.label,
        color: isRagColor(option.color) ? option.color.toLowerCase() : undefined,
      });
    }
    return acc;
  }, []);

const isRatedAnswer = (value) =>
  typeof value === 'object' && value !== null && 'value' in value && 'comment' in value;

export const getCustomFieldAnswerValue = (field, storedValue) =>
  isRatedAnswer(storedValue) ? storedValue.value : storedValue;

export const getCustomFieldAnswerComment = (field, storedValue) =>
  isRatedAnswer(storedValue) ? storedValue.comment : undefined;

export const getCustomFieldOptionColor = (field, storedValue) => {
  const answer = getCustomFieldAnswerValue(field, storedValue);
  if (typeof answer !== 'string') return undefined;
  return normalizeCustomFieldOptions(field.fieldRules?.options).find(
    (option) => option.id === answer
  )?.color;
};

export const getCustomFieldOptionLabel = (field, storedValue) => {
  const answer = getCustomFieldAnswerValue(field, storedValue);
  if (typeof answer !== 'string') return undefined;
  return normalizeCustomFieldOptions(field.fieldRules?.options).find(
    (option) => option.id === answer
  )?.label;
};

// ---- Input rendering ----

/**
 * @param {object} field   `{ fieldType, fieldHint, fieldRules }`
 * @param {object} context `{ companyId, hasRating }` -- what a field cannot
 *                         fetch for itself. Currency and AssetPicker load their
 *                         own lookups, so only companyId is threaded through.
 */
export const renderCustomFieldInput = (field, context = {}) => {
  const rules = field.fieldRules;
  const fieldOptions = normalizeCustomFieldOptions(rules?.options);
  const options = fieldOptions.map((option) => ({
    value: option.id,
    label: option.color ? (
      <span className="custom-field-option">
        <span className={`custom-field-option__dot custom-field-option__dot--${option.color}`} />
        {option.label}
      </span>
    ) : (
      option.label
    ),
  }));

  const placeholder = field.fieldHint ?? undefined;

  switch (field.fieldType) {
    case 'TextArea':
      return <Input.TextArea className="customField-Inputs" placeholder={placeholder} rows={3} />;
    case 'Number':
      return (
        <InputNumber
          className="customField-Inputs"
          style={{ width: '100%' }}
          placeholder={placeholder}
          inputMode="decimal"
          parser={(value) => (value ?? '').replace(/[^\d.-]/g, '')}
        />
      );
    case 'Select':
      return (
        <Select
          options={options}
          className="customField-Inputs"
          popupClassName="customField-Inputs-dropdown"
          placeholder={placeholder}
          allowClear
        />
      );
    case 'RadioButton':
      return <Radio.Group options={options} />;
    case 'Checkbox':
      return <CheckboxGroupField options={fieldOptions} showScoring={context.hasRating} />;
    case 'Badge':
      return <RagBadgeField colors={rules?.badgeColors} />;
    case 'Signature':
      return <SignatureField />;
    case 'LocationPicker':
      return <LocationPickerField />;
    case 'AssetPicker':
      return <AssetPickerField companyId={context.companyId} />;
    case 'Toggle':
      return <Switch />;
    case 'DatePicker':
      return (
        <DatePicker
          className="customField-Inputs"
          style={{ width: '100%' }}
          showTime={rules?.includeTime}
          placeholder={placeholder}
          inputReadOnly
        />
      );
    case 'TimePicker':
      return (
        <TimePicker
          style={{ width: '100%' }}
          format={TIME_FORMAT}
          className="customField-Inputs"
          placeholder={placeholder}
          inputReadOnly
        />
      );
    case 'FileUpload':
      return (
        <Upload maxCount={1} beforeUpload={() => false}>
          <Button icon={<UploadOutlined />} className="customField-Inputs">
            {placeholder ?? 'Upload File'}
          </Button>
        </Upload>
      );
    case 'Currency':
      return <CurrencyField placeholder={placeholder} />;
    case 'PhoneNumber':
      return <PhoneNumberField />;
    case 'Grid':
      return <GridField columns={rules?.columns} rows={rules?.rows} />;
    case 'Text':
    default:
      return <Input className="customField-Inputs" placeholder={placeholder} />;
  }
};

/** Form.Item needs `valuePropName="checked"` for switch-like controls. */
export const getCustomFieldValuePropName = (field) =>
  field.fieldType === 'Toggle' ? 'checked' : undefined;

// ---- Raw stored value <-> Form value ----

export const parseCustomFieldValueForForm = (field, rawValue) => {
  const value = getCustomFieldAnswerValue(field, rawValue);

  if (value === undefined || value === null) {
    switch (field.fieldType) {
      case 'Toggle':
        return false;
      case 'Checkbox':
        return [];
      default:
        return undefined;
    }
  }

  switch (field.fieldType) {
    case 'DatePicker':
      return typeof value === 'string' ? dayjs(value) : value;
    case 'TimePicker':
      return typeof value === 'string' ? dayjs(value, TIME_FORMAT) : value;
    default:
      return value;
  }
};

export const serializeCustomFieldFormValue = (field, formValue) => {
  if (formValue === undefined) return formValue;

  switch (field.fieldType) {
    case 'DatePicker':
      return dayjs.isDayjs(formValue) ? formValue.toISOString() : formValue;
    case 'TimePicker':
      return dayjs.isDayjs(formValue) ? formValue.format(TIME_FORMAT) : formValue;
    case 'Grid':
      return cleanGridValue(formValue);
    default:
      return formValue;
  }
};

// ---- Has the user actually filled this in? ----

export const isCustomFieldValueEmpty = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return true;

  let value = rawValue;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    // A plain (non-JSON) string that is not blank is filled -- e.g. Text, or an
    // ISO date, both of which throw here.
    try {
      value = JSON.parse(trimmed);
    } catch {
      return false;
    }
  }

  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number' || typeof value === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0;

  if (typeof value === 'object') {
    if ('registration' in value || 'fleetNo' in value) {
      return !Object.values(value).some(
        (entry) => entry !== null && entry !== undefined && String(entry).trim() !== ''
      );
    }
    if ('value' in value) {
      return value.value === null || value.value === undefined || value.value === '';
    }
    if ('lat' in value || 'lng' in value || 'address' in value) return !isLocationValue(value);
    if ('nationalNumber' in value) return String(value.nationalNumber ?? '').trim() === '';
    if ('fileList' in value) return !Array.isArray(value.fileList) || value.fileList.length === 0;
    return Object.keys(value).length === 0;
  }

  return false;
};

// ---- Raw stored value -> read-only display string ----

export const formatCustomFieldDisplayValue = (field, storedValue) => {
  const rawValue = getCustomFieldAnswerValue(field, storedValue);
  if (rawValue === null || rawValue === undefined || rawValue === '') return '—';

  switch (field.fieldType) {
    case 'LocationPicker':
      return formatLocationValue(rawValue);
    case 'DatePicker': {
      const date = dayjs(rawValue);
      if (!date.isValid()) return String(rawValue);
      return field.fieldRules?.includeTime
        ? date.format('DD MMM YYYY HH:mm')
        : date.format('DD MMM YYYY');
    }
    case 'TimePicker':
      return String(rawValue);
    case 'Badge': {
      const color = String(rawValue);
      return color ? color.charAt(0).toUpperCase() + color.slice(1) : '—';
    }
    case 'Signature':
      return rawValue ? 'Signed' : '—';
    case 'AssetPicker': {
      const summary = [rawValue?.registration, rawValue?.fleetNo, rawValue?.make, rawValue?.model]
        .filter(Boolean)
        .join(' · ');
      return summary || '—';
    }
    case 'Toggle':
      return rawValue ? 'Yes' : 'No';
    case 'Checkbox': {
      if (!Array.isArray(rawValue) || rawValue.length === 0) return '—';
      const byId = new Map(
        normalizeCustomFieldOptions(field.fieldRules?.options).map((option) => [
          option.id,
          option.label,
        ])
      );
      return rawValue.map((id) => byId.get(String(id)) ?? String(id)).join(', ');
    }
    case 'Currency': {
      if (rawValue?.value === undefined || rawValue?.value === null) return '—';
      try {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: rawValue.currency || 'ZAR',
        }).format(Number(rawValue.value));
      } catch {
        return `${rawValue.currency ?? ''} ${rawValue.value}`.trim();
      }
    }
    case 'PhoneNumber': {
      if (!rawValue?.nationalNumber) return '—';
      return `${rawValue.dialCode ?? ''} ${rawValue.nationalNumber}`.trim();
    }
    case 'FileUpload': {
      const names = (rawValue?.fileList ?? [])
        .map((file) => file?.name)
        .filter(Boolean)
        .join(', ');
      return names || '—';
    }
    case 'Select':
    case 'RadioButton':
      return getCustomFieldOptionLabel(field, rawValue) ?? String(rawValue);
    case 'Text':
    case 'TextArea':
    case 'Number':
    default:
      return String(rawValue);
  }
};
